import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_ICON_URL } from "@/lib/assets";
import { useLanguage } from "@/i18n/LanguageContext";

const linkDefs = [
  { key: "studio", href: "#about" },
  { key: "philosophie", href: "#philosophy" },
  { key: "signature", href: "#signature" },
  { key: "savoirFaire", href: "#services" },
  { key: "galerie", href: "#gallery" },
  { key: "temoignages", href: "#testimonials" },
  { key: "contact", href: "#contact" },
];

export const Navbar = () => {
  const { t, lang, setLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const links = linkDefs.map((l) => ({ ...l, label: t(`nav.${l.key}`) }));

  const LangToggle = ({ testid }) => (
    <div
      data-testid={testid}
      className="flex items-center text-xs tracking-widest uppercase border border-white/15 px-2.5 py-1.5"
    >
      {["fr", "en", "lb"].map((l, i) => (
        <span key={l} className="flex items-center">
          {i > 0 && <span className="mx-1.5 opacity-30">/</span>}
          <button
            onClick={() => setLang(l)}
            data-testid={`${testid}-${l}`}
            className={`transition-colors duration-300 ${
              lang === l ? "text-[#D4AF37]" : "text-[#A0ABC0] hover:text-white"
            }`}
          >
            {l.toUpperCase()}
          </button>
        </span>
      ))}
    </div>
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0A1420]/85 backdrop-blur-md border-b border-white/10 py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 flex items-center justify-between">
        <button
          onClick={() => go("#hero")}
          data-testid="nav-home"
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_ICON_URL}
            alt=""
            aria-hidden="true"
            className="h-8 w-8 sm:h-9 sm:w-9 object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <span className="hidden sm:block font-serif-lux text-sm tracking-[0.28em] uppercase text-[#A0ABC0] group-hover:text-[#D4AF37] transition-colors duration-300">
            Medernach Lionel
          </span>
        </button>

        <nav className="hidden xl:flex items-center gap-4 2xl:gap-6">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-xs whitespace-nowrap text-[#A0ABC0] hover:text-[#D4AF37] transition-colors duration-300 tracking-[0.12em] uppercase"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#consultation")}
            data-testid="nav-cta"
            className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1420] transition-colors duration-300 px-4 py-2.5 uppercase tracking-[0.12em] text-[11px] whitespace-nowrap"
          >
            {t("nav.cta")}
          </button>
          <LangToggle testid="lang-toggle-desktop" />
        </nav>

        <div className="xl:hidden flex items-center gap-3">
          <LangToggle testid="lang-toggle-mobile" />
          <button
            className="text-[#D4AF37]"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.nav
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="xl:hidden bg-[#0A1420]/95 backdrop-blur-md border-t border-white/10 mt-4"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col px-8 py-6 gap-5">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="text-left text-[#A0ABC0] hover:text-[#D4AF37] tracking-widest uppercase text-sm"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("#consultation")}
              data-testid="mobile-nav-cta"
              className="mt-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1420] transition-colors duration-300 px-6 py-3 uppercase tracking-widest text-xs text-center"
            >
              {t("nav.cta")}
            </button>
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
};
